/**
 * CategoryBadges - Badges visuales de categorías/clasificaciones
 * Muestra categorías con colores consistentes entre perspectivas
 */

import React from 'react';
import { Tag, Users, MapPin, Clock, Scale } from 'lucide-react';

// Colores consistentes para cada tipo de categoría
const CATEGORY_COLORS = {
  // Tipos de mitzvah
  type: {
    D: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300', label: 'Creencias/Opiniones' },
    P: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300', label: 'Acciones' },
    M: { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-300', label: 'Medidas/Rasgo' },
    R: { bg: 'bg-pink-100', text: 'text-pink-800', border: 'border-pink-300', label: 'Discurso/Habla' }
  },
  
  // A quién aplica
  whoApplies: {
    A: { bg: 'bg-indigo-100', text: 'text-indigo-800', border: 'border-indigo-300', label: 'Todos' },
    M: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300', label: 'Hombres' },
    W: { bg: 'bg-pink-100', text: 'text-pink-800', border: 'border-pink-300', label: 'Mujeres' },
    Y: { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-300', label: 'Israel' },
    I: { bg: 'bg-cyan-100', text: 'text-cyan-800', border: 'border-cyan-300', label: 'Israel-Hombre' },
    L: { bg: 'bg-teal-100', text: 'text-teal-800', border: 'border-teal-300', label: 'Levita' },
    K: { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300', label: 'Sacerdote' },
    D: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300', label: 'Sumo Sacerdote' },
    G: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300', label: 'Rey' },
    B: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300', label: 'Corte' },
    Z: { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-300', label: 'Extranjeros' }
  },
  
  // Castigos
  punishment: {
    N: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300', label: 'Ninguno' },
    K: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300', label: 'Corte' },
    B: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300', label: 'Muerte Celestial' },
    S: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300', label: 'Lapidación' },
    F: { bg: 'bg-red-200', text: 'text-red-900', border: 'border-red-400', label: 'Fuego' },
    C: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300', label: 'Estrangulamiento' },
    H: { bg: 'bg-red-200', text: 'text-red-900', border: 'border-red-400', label: 'Asesinato' },
    M: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300', label: 'Azotes' }
  },
  
  // Dónde aplica (Chinuch)
  whereApplies: {
    0: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300', label: 'En todos los lugares' },
    1: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300', label: 'En Israel' },
    2: { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-300', label: 'En Jerusalén' },
    3: { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300', label: 'En el Santo Templo' }
  },
  
  // Cuándo aplica (Chinuch)
  whenApplies: {
    0: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300', label: 'En todo tiempo' },
    1: { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-300', label: 'Con verdadera Smicha' },
    2: { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300', label: 'Cuando se construya el Templo' },
    3: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300', label: 'En año del Jubileo' }
  }
};

const CategoryBadges = ({ perspectiveKey, perspective }) => {
  const badges = [];

  // Rambam
  if (perspectiveKey === 'rambam') {
    // Tipo
    if (perspective.type && CATEGORY_COLORS.type[perspective.type]) {
      const typeColor = CATEGORY_COLORS.type[perspective.type];
      badges.push({
        icon: <Tag className="w-4 h-4" />,
        label: 'Tipo',
        value: typeColor.label,
        ...typeColor
      });
    }

    // A quién aplica
    if (perspective.whoAppliesId && CATEGORY_COLORS.whoApplies[perspective.whoAppliesId]) {
      const whoColor = CATEGORY_COLORS.whoApplies[perspective.whoAppliesId];
      badges.push({
        icon: <Users className="w-4 h-4" />,
        label: 'Aplica a',
        value: whoColor.label,
        ...whoColor
      });
    }

    // Castigo
    if (perspective.punishmentId && CATEGORY_COLORS.punishment[perspective.punishmentId]) {
      const punishmentColor = CATEGORY_COLORS.punishment[perspective.punishmentId];
      badges.push({
        icon: <Scale className="w-4 h-4" />,
        label: 'Castigo',
        value: punishmentColor.label,
        ...punishmentColor
      });
    }
  }

  // Chinuch
  if (perspectiveKey === 'chinuch') {
    // Dónde aplica
    if (perspective.whereApplies !== undefined && CATEGORY_COLORS.whereApplies[perspective.whereApplies]) {
      const whereColor = CATEGORY_COLORS.whereApplies[perspective.whereApplies];
      badges.push({
        icon: <MapPin className="w-4 h-4" />,
        label: 'Dónde',
        value: whereColor.label,
        ...whereColor
      });
    }

    // Cuándo aplica
    if (perspective.whenApplies !== undefined && CATEGORY_COLORS.whenApplies[perspective.whenApplies]) {
      const whenColor = CATEGORY_COLORS.whenApplies[perspective.whenApplies];
      badges.push({
        icon: <Clock className="w-4 h-4" />,
        label: 'Cuándo',
        value: whenColor.label,
        ...whenColor
      });
    }

    // A quién aplica (Chinuch usa números)
    if (perspective.whoApplies !== undefined) {
      const chinuchWhoAppliesLabels = {
        0: { label: 'Hombres y mujeres', code: 'A' },
        1: { label: 'Hombres', code: 'M' },
        2: { label: 'Sabios', code: 'A' },
        3: { label: 'Israelitas', code: 'Y' },
        4: { label: 'Jueces', code: 'B' },
        5: { label: 'Cohanim', code: 'K' },
        6: { label: 'Toda la congregación', code: 'A' },
        7: { label: 'Sanedrín', code: 'B' },
        8: { label: 'Cohen Gadol', code: 'D' },
        9: { label: 'Mujeres', code: 'W' },
        10: { label: 'No Cohanim', code: 'Y' },
        11: { label: 'Cohanim hombres y mujeres', code: 'K' },
        12: { label: 'Leviim', code: 'L' },
        13: { label: 'Rey', code: 'G' }
      };

      const whoData = chinuchWhoAppliesLabels[perspective.whoApplies];
      if (whoData && CATEGORY_COLORS.whoApplies[whoData.code]) {
        const whoColor = CATEGORY_COLORS.whoApplies[whoData.code];
        badges.push({
          icon: <Users className="w-4 h-4" />,
          label: 'Aplica a',
          value: whoData.label,
          ...whoColor
        });
      }
    }

    // Castigo (Chinuch usa números)
    if (perspective.punishment !== undefined) {
      const chinuchPunishmentMap = {
        0: 'N', // Nada
        1: 'M', // Azotes
        2: 'K', // Cortes
        3: 'S', // Lapidación
        4: 'H', // Decapitación
        5: 'C', // Estrangulamiento
        6: 'B', // Muerte celestial
        7: 'F'  // Muerte por fuego
      };

      const punishmentCode = chinuchPunishmentMap[perspective.punishment];
      if (punishmentCode && CATEGORY_COLORS.punishment[punishmentCode]) {
        const punishmentColor = CATEGORY_COLORS.punishment[punishmentCode];
        badges.push({
          icon: <Scale className="w-4 h-4" />,
          label: 'Castigo',
          value: punishmentColor.label,
          ...punishmentColor
        });
      }
    }
  }

  // SEMAG
  if (perspectiveKey === 'semag') {
    // Categoría SEMAG (mostrar nombre real desde categoryData)
    if (perspective.categoryData) {
      // Tenemos el nombre real de la categoría
      const categoryColor = {
        bg: 'bg-green-100',
        text: 'text-green-800',
        border: 'border-green-300'
      };

      badges.push({
        icon: <Tag className="w-4 h-4" />,
        label: 'Categoría SEMAG',
        value: perspective.categoryData.nameEs || perspective.categoryData.nameEn || `Categoría ${perspective.category}`,
        ...categoryColor
      });
    } else if (perspective.category !== null && perspective.category !== undefined) {
      // Fallback: mostrar solo el número si no hay categoryData
      const categoryColor = {
        bg: 'bg-green-100',
        text: 'text-green-800',
        border: 'border-green-300'
      };

      badges.push({
        icon: <Tag className="w-4 h-4" />,
        label: 'Categoría SEMAG',
        value: `Categoría ${perspective.category}`,
        ...categoryColor
      });
    }
  }

  // Hebrew Roots (ya se muestra positivo/negativo en PerspectivePanel, no necesita badges adicionales aquí)

  if (badges.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
        Clasificaciones
      </p>
      <div className="flex flex-wrap gap-2">
        {badges.map((badge, index) => (
          <div
            key={index}
            className={`
              inline-flex items-center gap-2 px-3 py-2 rounded-lg border-2
              ${badge.bg} ${badge.text} ${badge.border}
              font-medium text-sm shadow-sm
            `}
          >
            {badge.icon}
            <span className="font-semibold">{badge.label}:</span>
            <span>{badge.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CategoryBadges;

